/**
 * Abstract base for React Query hooks-based repositories: keeps hooks as plain
 * functions (React requirement) while sharing optimistic-update / invalidation logic via inheritance.
 */

import type {
  QueryClient,
  QueryKey,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { useQuery, useMutation } from "@tanstack/react-query";

const REFETCH_ON_MOUNT_VALUES = [true, false, "always"] as const;
export type RefetchOnMountType = (typeof REFETCH_ON_MOUNT_VALUES)[number];

export type RefetchIntervalValue = number | false;

export type RefetchIntervalFn<TDomain> = (
  data: TDomain | undefined,
) => RefetchIntervalValue;

export interface OptimisticUpdateContext<T> {
  readonly previous: T | undefined;
  readonly rollback: () => void;
}

export interface OptimisticUpdateOptions<T> {
  readonly queryKey: QueryKey;
  readonly updater: (old: T) => T;
}

export abstract class BaseApiHooksRepository {
  constructor(protected readonly queryClient: QueryClient) {}

  protected async prepareOptimisticUpdate<T>(
    options: OptimisticUpdateOptions<T>,
  ): Promise<OptimisticUpdateContext<T>> {
    const { queryKey, updater } = options;

    // Cancel any in-flight queries to prevent race conditions
    await this.queryClient.cancelQueries({ queryKey });

    const previous = this.queryClient.getQueryData<T>(queryKey);

    if (previous !== undefined) {
      this.queryClient.setQueryData<T>(queryKey, updater(previous));
    }

    return {
      previous,
      rollback: (): void => {
        if (previous !== undefined) {
          this.queryClient.setQueryData(queryKey, previous);
        }
      },
    };
  }

  protected async prepareMultipleOptimisticUpdates<T>(
    updates: readonly OptimisticUpdateOptions<T>[],
  ): Promise<{
    contexts: readonly OptimisticUpdateContext<T>[];
    rollbackAll: () => void;
  }> {
    const contexts = await Promise.all(
      updates.map((update) => this.prepareOptimisticUpdate(update)),
    );

    return {
      contexts,
      rollbackAll: (): void => {
        for (const ctx of contexts) {
          ctx.rollback();
        }
      },
    };
  }

  protected invalidateQueries(queryKey: QueryKey): void {
    void this.queryClient.invalidateQueries({ queryKey });
  }

  protected invalidateMultipleQueries(queryKeys: readonly QueryKey[]): void {
    for (const queryKey of queryKeys) {
      this.invalidateQueries(queryKey);
    }
  }

  protected getCachedData<T>(queryKey: QueryKey): T | undefined {
    return this.queryClient.getQueryData<T>(queryKey);
  }

  protected setCachedData<T>(queryKey: QueryKey, data: T): void {
    this.queryClient.setQueryData<T>(queryKey, data);
  }

  /**
   * Creates a typed query hook with DTO→domain parsing.
   *
   * Wraps useQuery with a queryFn that fetches the DTO and applies the parse
   * function to produce the domain type. Supports all common useQuery options.
   *
   * Uses explicit return type and internal cast because React Query's
   * NonFunctionGuard/NoInfer wrappers reject unconstrained generic type params.
   * Domain types are data objects (never functions), so the cast is safe.
   */
  protected query<TDto, TDomain>(options: {
    readonly key: QueryKey;
    readonly fn: () => Promise<TDto>;
    readonly parse: (dto: TDto) => TDomain;
    readonly enabled?: boolean;
    readonly staleTime?: number;
    readonly retry?: number;
    readonly refetchOnMount?: RefetchOnMountType;
    readonly refetchInterval?: RefetchIntervalFn<TDomain>;
    readonly initialData?: () => TDomain | undefined;
    readonly placeholderData?: (
      previousData: TDomain | undefined,
    ) => TDomain | undefined;
  }): UseQueryResult<TDomain> {
    const userRefetchInterval = options.refetchInterval;
    const refetchIntervalAdapter =
      userRefetchInterval === undefined
        ? undefined
        : // biome-ignore lint/suspicious/noExplicitAny: React Query's NonFunctionGuard types `query.state.data` as `any`; runtime shape is TDomain | undefined
          (query: { state: { data: any } }): RefetchIntervalValue =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- `query.state.data` is TDomain | undefined at runtime
            userRefetchInterval(query.state.data);
    // biome-ignore lint/suspicious/noExplicitAny: React Query's NonFunctionGuard<T> rejects unconstrained generics; domain types are never functions
    const queryOptions: any = {
      queryKey: options.key,
      queryFn: async () => options.parse(await options.fn()),
      enabled: options.enabled,
      staleTime: options.staleTime,
      retry: options.retry,
      refetchOnMount: options.refetchOnMount,
      refetchInterval: refetchIntervalAdapter,
      initialData: options.initialData,
      placeholderData: options.placeholderData,
    };
    // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-argument -- called from hook-factory methods in subclasses; cast required for NonFunctionGuard workaround
    return useQuery(queryOptions) as UseQueryResult<TDomain>;
  }

  protected mutation<TDto, TDomain, TVars>(options: {
    readonly fn: (vars: TVars) => Promise<TDto>;
    readonly parse: (dto: TDto) => TDomain;
    readonly onSuccess?: (data: TDomain, vars: TVars) => void;
    readonly onError?: (error: Error, vars: TVars) => void;
  }): UseMutationResult<TDomain, Error, TVars> {
    // biome-ignore lint/suspicious/noExplicitAny: same NonFunctionGuard issue as query()
    const mutationOptions: any = {
      mutationFn: async (vars: TVars) => options.parse(await options.fn(vars)),
      ...(options.onSuccess !== undefined && {
        onSuccess: options.onSuccess,
      }),
      ...(options.onError !== undefined && {
        onError: options.onError,
      }),
    };
    // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-argument -- same as query()
    return useMutation(mutationOptions) as UseMutationResult<
      TDomain,
      Error,
      TVars
    >;
  }

  protected voidMutation<TVars>(options: {
    readonly fn: (vars: TVars) => Promise<void>;
    readonly onSuccess?: (data: void, vars: TVars) => void;
    readonly onError?: (error: Error, vars: TVars) => void;
  }): UseMutationResult<void, Error, TVars> {
    // biome-ignore lint/suspicious/noExplicitAny: same NonFunctionGuard issue as query()
    const mutationOptions: any = {
      mutationFn: options.fn,
      ...(options.onSuccess !== undefined && {
        onSuccess: options.onSuccess,
      }),
      ...(options.onError !== undefined && {
        onError: options.onError,
      }),
    };
    // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-argument -- same as query()
    return useMutation(mutationOptions) as UseMutationResult<
      void,
      Error,
      TVars
    >;
  }
}
